/** @jsxImportSource theme-ui */
import React from 'react';
import { SxProp } from 'theme-ui';

import { PreventabilityDisruptionFactorType } from 'client/dto';
import theme from 'theme';

import Typography, { TypographyColors } from 'components/_common/Typography/Typography';

import { BdNoDataIcon } from '../BdNoDataIcon';

const CreateSvgVaccineAvailable = (
  color: keyof typeof TypographyColors,
  backgroundColor: keyof typeof TypographyColors,
  backgroundOpacity = 0.15
) => {
  return (
    <svg
      data-testid="vaccine-available"
      width="36"
      height="36"
      viewBox="0 0 36 36"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        opacity={backgroundOpacity.toString()}
        cx="18"
        cy="18"
        r="15"
        fill={theme.colors[backgroundColor]}
      />
      <path
        d="M26 26L23 23"
        stroke={theme.colors[color]}
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15.7857 12.5608L22.7351 19.2652C23.6227 20.1529 23.6227 21.6126 22.7351 22.5003C21.8474 23.388 20.3877 23.388 19.5 22.5003L12.5506 15.7959C12.5111 15.7564 12.5111 15.6874 12.5506 15.648L15.6278 12.5707C15.6772 12.5115 15.7462 12.5115 15.7857 12.5608Z"
        stroke={theme.colors[color]}
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.3009 12.6622L13.4141 14.7754L14.781 13.4085L12.6678 11.2953L11.3009 12.6622Z"
        fill={theme.colors[color]}
      />
      <path
        d="M9.49316 14.3262L14.3162 9.4933"
        stroke={theme.colors[color]}
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const CreateSvgNoVaccineAvailable = (
  color: keyof typeof TypographyColors,
  backgroundColor: keyof typeof TypographyColors,
  lineColor: keyof typeof TypographyColors,
  backgroundOpacity = 0.15
) => {
  return (
    <svg
      data-testid="vaccine-not-available"
      width="36"
      height="36"
      viewBox="0 0 36 36"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        opacity={backgroundOpacity.toString()}
        cx="18"
        cy="18"
        r="15"
        fill={theme.colors[backgroundColor]}
      />
      <g opacity="0.7">
        <path
          d="M26 26L23 23"
          stroke={theme.colors[color]}
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M15.7857 12.5608L22.7351 19.2652C23.6227 20.1529 23.6227 21.6126 22.7351 22.5003C21.8474 23.388 20.3877 23.388 19.5 22.5003L12.5506 15.7959C12.5111 15.7564 12.5111 15.6874 12.5506 15.648L15.6278 12.5707C15.6772 12.5115 15.7462 12.5115 15.7857 12.5608Z"
          stroke={theme.colors[color]}
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M11.3009 12.6622L13.4141 14.7754L14.781 13.4085L12.6678 11.2953L11.3009 12.6622Z"
          fill={theme.colors[color]}
        />
        <path
          d="M9.49316 14.3262L14.3162 9.4933"
          stroke={theme.colors[color]}
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <line
        x1="28.5425"
        y1="6.51786"
        x2="7.54252"
        y2="28.5179"
        stroke={theme.colors[lineColor]}
        strokeWidth="1.5"
      />
    </svg>
  );
};

const getIcon = (type: PreventabilityDisruptionFactorType, dark: boolean) => {
  switch (type) {
    case PreventabilityDisruptionFactorType.NoData:
      return <BdNoDataIcon dark={dark} />;
    case PreventabilityDisruptionFactorType.WideProphylaxis:
      return CreateSvgVaccineAvailable(dark ? 'stone20' : 'stone70', 'stone50', 0.15);
    case PreventabilityDisruptionFactorType.WideVaccine:
      return CreateSvgVaccineAvailable(dark ? 'stone20' : 'stone70', 'stone50', 0.15);
    case PreventabilityDisruptionFactorType.LimitedVaccine:
      return CreateSvgVaccineAvailable(dark ? 'saffron20' : 'saffron70', 'saffron70', 0.15);
    case PreventabilityDisruptionFactorType.NoVaccineOrProphylaxis:
      return CreateSvgNoVaccineAvailable(
        dark ? 'starfish40' : 'starfish100',
        'starfish100',
        'starfish100',
        0.15
      );
    default:
      return <BdNoDataIcon dark={dark} />;
  }
};

const getDescription = (type: PreventabilityDisruptionFactorType) => {
  switch (type) {
    case PreventabilityDisruptionFactorType.WideProphylaxis:
      return 'Wide prophylaxis availability';
    case PreventabilityDisruptionFactorType.WideVaccine:
      return 'Wide vaccine availability';
    case PreventabilityDisruptionFactorType.LimitedVaccine:
      return 'Limited vaccine availability';
    case PreventabilityDisruptionFactorType.NoVaccineOrProphylaxis:
      return 'No vaccine or prophylaxis availability';
    case PreventabilityDisruptionFactorType.NoData:
    default:
      return 'No data available';
  }
};

type BdPreventabilityIconProps = SxProp & {
  type: PreventabilityDisruptionFactorType;
  /** For dark backgrounds */
  dark?: boolean;
  labelPosition?: 'top' | 'bottom';
  /** Hides the text on the icon */
  simple?: boolean;
};

const BdPreventabilityIcon: React.FC<BdPreventabilityIconProps> = ({
  type,
  dark = false,
  labelPosition = 'bottom',
  simple = false,
  ...props
}) => {
  return (
    <div data-testid="preventability-icon" sx={{ display: 'flex' }} {...props}>
      {getIcon(type, dark)}
      {!simple && (
        <div
          data-testid="description"
          sx={{
            display: 'flex',
            flexDirection: labelPosition === 'bottom' ? 'column' : 'column-reverse',
            ml: '7px',
          }}
        >
          <Typography
            variant="subtitle2"
            color={dark ? 'stone10' : 'stone90'}
            marginBottom={labelPosition === 'bottom' ? '4px' : '0'}
          >
            {getDescription(type)}
          </Typography>
          <Typography
            variant="caption"
            color={dark ? 'deepSea40' : 'stone90'}
            marginBottom={labelPosition === 'bottom' ? '0' : '4px'}
          >
            Preventability
          </Typography>
        </div>
      )}
    </div>
  );
};

export default BdPreventabilityIcon;
